import type { MapGeometry, MapLocation } from "../shared/types";
import type { RoutingDocument } from "./venueMapRoutingWorkerProtocol";
import { venueMapPinAnchor } from "./venueMapPinAnchor";
import { createVenueMapRoutingConstraints } from "./venueMapRoutingConstraints";

export interface VenueMapRoutePoint {
  x: number;
  y: number;
}

export interface VenueMapRouteSegment {
  start: VenueMapRoutePoint;
  end: VenueMapRoutePoint;
  control?: VenueMapRoutePoint;
}

type Point = VenueMapRoutePoint;

interface PreparedAisleGrid {
  searchNodeCount: number;
  routePoint(index: number): Point;
  isAvailable(index: number): boolean;
  connections(point: Point, includeDoors?: boolean): number[];
  doorwayLinks: Map<number, number[]>;
  clearNeighbor(a: number, b: number, from: Point, to: Point): boolean;
}

function createRoutingContext(
  geometry: MapGeometry,
  locations: readonly MapLocation[],
) {
  // These are drawing units, not surveyed distances. Prefer a comfortable aisle
  // inset, retaining a smaller safety inset only when a narrow access requires it.
  const CLEARANCE = 2;
  const PREFERRED_CLEARANCE = 7;
  const OPEN_AISLE_CLEARANCE = 13;
  const MAX_AISLE_SHIFT = 7;
  const GRID_STEP = Math.max(
    2,
    Math.sqrt((geometry.floor.width * geometry.floor.height) / 120_000),
  );
  const EPSILON = 1e-8;
  const floor = geometry.floor;
  const constraints = createVenueMapRoutingConstraints(geometry);
  const minX = floor.x + CLEARANCE;
  const minY = floor.y + CLEARANCE;
  const maxX = floor.x + floor.width - CLEARANCE;
  const maxY = floor.y + floor.height - CLEARANCE;
  const columns = Math.max(2, Math.ceil((maxX - minX) / GRID_STEP) + 1);
  const rows = Math.max(2, Math.ceil((maxY - minY) / GRID_STEP) + 1);
  const stepX = Math.max(EPSILON, (maxX - minX) / (columns - 1));
  const stepY = Math.max(EPSILON, (maxY - minY) / (rows - 1));
  const nodeCount = columns * rows;
  const knownLocations = new Map(
    locations.map((location) => [location.id, location]),
  );
  const routeCache = new Map<string, VenueMapRoutePoint[] | null>();
  // Prepared data belongs to this immutable map snapshot. Only the two routing
  // clearances can populate these caches; simple direct legs never need a grid.
  const aisleGrids = new Map<number, PreparedAisleGrid>();
  const locationAccess = new Map<
    string,
    {
      x: number;
      y: number;
      anchor: Point;
      explicit: Point | undefined;
      points: Map<number, Point[]>;
    }
  >();
  const obstacles = geometry.stands.flatMap((stand) =>
    stand.polygons.map((polygon) => ({
      id: stand.id,
      points: polygon.map(([x, y]) => ({ x, y })),
      minX: Math.min(...polygon.map(([x]) => x)) - OPEN_AISLE_CLEARANCE,
      minY: Math.min(...polygon.map(([, y]) => y)) - OPEN_AISLE_CLEARANCE,
      maxX: Math.max(...polygon.map(([x]) => x)) + OPEN_AISLE_CLEARANCE,
      maxY: Math.max(...polygon.map(([, y]) => y)) + OPEN_AISLE_CLEARANCE,
    })),
  );

  function distance(a: Point, b: Point): number {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function pointSegmentDistanceSquared(p: Point, a: Point, b: Point): number {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSquared = dx * dx + dy * dy;
    const t = lengthSquared
      ? Math.max(
          0,
          Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared),
        )
      : 0;
    return (p.x - a.x - t * dx) ** 2 + (p.y - a.y - t * dy) ** 2;
  }

  function cross(a: Point, b: Point, c: Point): number {
    return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  }

  function segmentsIntersect(a: Point, b: Point, c: Point, d: Point): boolean {
    if (
      Math.max(a.x, b.x) < Math.min(c.x, d.x) ||
      Math.max(c.x, d.x) < Math.min(a.x, b.x) ||
      Math.max(a.y, b.y) < Math.min(c.y, d.y) ||
      Math.max(c.y, d.y) < Math.min(a.y, b.y)
    )
      return false;
    return (
      cross(a, b, c) * cross(a, b, d) <= 0 &&
      cross(c, d, a) * cross(c, d, b) <= 0
    );
  }

  function segmentDistanceSquared(
    a: Point,
    b: Point,
    c: Point,
    d: Point,
  ): number {
    if (segmentsIntersect(a, b, c, d)) return 0;
    return Math.min(
      pointSegmentDistanceSquared(a, c, d),
      pointSegmentDistanceSquared(b, c, d),
      pointSegmentDistanceSquared(c, a, b),
      pointSegmentDistanceSquared(d, a, b),
    );
  }

  function insidePolygon(point: Point, polygon: readonly Point[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const a = polygon[i];
      const b = polygon[j];
      if (
        a.y > point.y !== b.y > point.y &&
        point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x
      )
        inside = !inside;
    }
    return inside;
  }

  function insideFloor(point: Point): boolean {
    if (point.x < minX || point.x > maxX || point.y < minY || point.y > maxY)
      return false;
    const radius = Math.max(0, floor.radius - CLEARANCE);
    const x = Math.max(minX + radius, Math.min(maxX - radius, point.x));
    const y = Math.max(minY + radius, Math.min(maxY - radius, point.y));
    return (point.x - x) ** 2 + (point.y - y) ** 2 <= radius ** 2 + EPSILON;
  }

  function gridPoint(index: number): Point {
    return {
      x: minX + (index % columns) * stepX,
      y: minY + Math.floor(index / columns) * stepY,
    };
  }

  function clearSegment(
    a: Point,
    b: Point,
    clearance: number,
    enteredStand?: string,
  ): boolean {
    if (!insideFloor(a) || !insideFloor(b)) return false;
    if (constraints && !constraints.clearSegment(a, b, clearance, enteredStand))
      return false;
    const segmentMinX = Math.min(a.x, b.x);
    const segmentMinY = Math.min(a.y, b.y);
    const segmentMaxX = Math.max(a.x, b.x);
    const segmentMaxY = Math.max(a.y, b.y);
    for (const obstacle of obstacles) {
      if (
        obstacle.id === enteredStand ||
        segmentMaxX < obstacle.minX ||
        segmentMinX > obstacle.maxX ||
        segmentMaxY < obstacle.minY ||
        segmentMinY > obstacle.maxY
      )
        continue;
      if (
        insidePolygon(a, obstacle.points) ||
        insidePolygon(b, obstacle.points)
      )
        return false;
      for (let i = 0; i < obstacle.points.length; i++) {
        if (
          segmentDistanceSquared(
            a,
            b,
            obstacle.points[i],
            obstacle.points[(i + 1) % obstacle.points.length],
          ) <
          clearance ** 2 - EPSILON
        )
          return false;
      }
    }
    return true;
  }

  function removeCollinearPoints(points: Point[]): Point[] {
    const result: Point[] = [];
    for (const point of points) {
      if (result.length && distance(result.at(-1)!, point) <= EPSILON) continue;
      while (result.length > 1) {
        const a = result.at(-2)!;
        const b = result.at(-1)!;
        if (
          Math.abs(cross(a, b, point)) > EPSILON ||
          (b.x - a.x) * (point.x - b.x) + (b.y - a.y) * (point.y - b.y) < 0
        )
          break;
        result.pop();
      }
      result.push(point);
    }
    return result;
  }

  /** Merge small aisle offsets into one continuous run wherever the rows overlap. */
  function alignAisleRuns(
    points: Point[],
    clearance: number,
    fromId: string,
    toId: string,
    locked: readonly Point[] = [],
  ): Point[] {
    const aligned = points.map((point) => ({ ...point }));
    for (let i = 1; i + 4 < aligned.length; i++) {
      const [a, b, c, d] = aligned.slice(i, i + 4);
      if (
        locked.some((lock) =>
          [a, b, c, d].some((point) => distance(lock, point) < EPSILON),
        )
      )
        continue;
      const horizontal =
        Math.abs(a.y - b.y) < EPSILON &&
        Math.abs(c.y - d.y) < EPSILON &&
        (b.x - a.x) * (d.x - c.x) > 0;
      const vertical =
        Math.abs(a.x - b.x) < EPSILON &&
        Math.abs(c.x - d.x) < EPSILON &&
        (b.y - a.y) * (d.y - c.y) > 0;
      if ((!horizontal && !vertical) || distance(b, c) > MAX_AISLE_SHIFT * 2)
        continue;
      const axis = horizontal ? "y" : "x";
      for (const coordinate of [(a[axis] + d[axis]) / 2, a[axis], d[axis]]) {
        const nextA = { ...a, [axis]: coordinate };
        const nextD = { ...d, [axis]: coordinate };
        if (
          clearSegment(nextA, nextD, clearance) &&
          clearSegment(
            aligned[i - 1],
            nextA,
            i === 1 ? CLEARANCE : clearance,
            i === 1 ? fromId : undefined,
          ) &&
          clearSegment(
            nextD,
            aligned[i + 4],
            i + 4 === aligned.length - 1 ? CLEARANCE : clearance,
            i + 4 === aligned.length - 1 ? toId : undefined,
          )
        ) {
          aligned.splice(i, 4, nextA, nextD);
          i = Math.max(0, i - 2);
          break;
        }
      }
    }
    return aligned;
  }

  /** Center narrow aisle runs and give open aisles breathing room. */
  function spaceOpenAisles(
    points: Point[],
    clearance: number,
    fromId: string,
    toId: string,
    locked: readonly Point[] = [],
  ): Point[] {
    const spaced = points.map((point) => ({ ...point }));
    const clearJoin = (a: Point, b: Point, index: number) =>
      index === 0 || index === points.length - 2
        ? clearSegment(a, b, CLEARANCE, index === 0 ? fromId : toId)
        : clearSegment(a, b, clearance);
    // Only translate long aisle runs. Their short joining segments absorb the
    // offset, and both pin anchors remain exactly where they were.
    for (let i = 1; i < spaced.length - 2; i++) {
      const a = spaced[i];
      const b = spaced[i + 1];
      if (
        locked.some(
          (lock) => distance(lock, a) < EPSILON || distance(lock, b) < EPSILON,
        )
      )
        continue;
      const length = distance(a, b);
      if (length < OPEN_AISLE_CLEARANCE * 2) continue;
      // The ends may join a narrower cross-aisle. Measure the open run away
      // from those junctions while checking its full length at the safe inset.
      const clearRun = (start: Point, end: Point, margin: number) => {
        const trim = Math.min(OPEN_AISLE_CLEARANCE, length / 3) / length;
        return (
          clearSegment(start, end, clearance) &&
          clearSegment(
            {
              x: start.x + (end.x - start.x) * trim,
              y: start.y + (end.y - start.y) * trim,
            },
            {
              x: end.x + (start.x - end.x) * trim,
              y: end.y + (start.y - end.y) * trim,
            },
            margin,
          )
        );
      };
      if (clearRun(a, b, OPEN_AISLE_CLEARANCE)) {
        continue;
      }
      const normal = { x: (a.y - b.y) / length, y: (b.x - a.x) / length };
      // A narrower aisle cannot offer thirteen units on both sides. Choose its
      // largest safe inset instead, moving the whole run parallel to the booths.
      let bestMargin = clearance;
      let best: [Point, Point] | undefined;
      for (
        let margin = OPEN_AISLE_CLEARANCE;
        margin > clearance;
        margin -= 0.5
      ) {
        if (clearRun(a, b, margin)) {
          bestMargin = margin;
          break;
        }
      }
      for (let shift = 0.5; shift <= MAX_AISLE_SHIFT; shift += 0.5) {
        for (const direction of [1, -1]) {
          const offset = {
            x: normal.x * shift * direction,
            y: normal.y * shift * direction,
          };
          const nextA = { x: a.x + offset.x, y: a.y + offset.y };
          const nextB = { x: b.x + offset.x, y: b.y + offset.y };
          if (
            Math.abs(nextA.x - points[i].x) <= MAX_AISLE_SHIFT + EPSILON &&
            Math.abs(nextA.y - points[i].y) <= MAX_AISLE_SHIFT + EPSILON &&
            Math.abs(nextB.x - points[i + 1].x) <= MAX_AISLE_SHIFT + EPSILON &&
            Math.abs(nextB.y - points[i + 1].y) <= MAX_AISLE_SHIFT + EPSILON &&
            clearJoin(spaced[i - 1], nextA, i - 1) &&
            clearJoin(nextB, spaced[i + 2], i + 1)
          ) {
            for (
              let margin = OPEN_AISLE_CLEARANCE;
              margin > bestMargin;
              margin -= 0.5
            ) {
              if (clearRun(nextA, nextB, margin)) {
                best = [nextA, nextB];
                bestMargin = margin;
                break;
              }
            }
          }
        }
        if (bestMargin === OPEN_AISLE_CLEARANCE) break;
      }
      if (best) {
        [spaced[i], spaced[i + 1]] = best;
      }
    }
    return spaced;
  }

  /** Round in floor coordinates so the same safe bends survive zoom and rotation. */
  function roundedMapRouteSegments(
    points: readonly VenueMapRoutePoint[],
  ): VenueMapRouteSegment[] {
    if (points.length < 2) return [];
    const floorPoints = points.map(({ x, y }) => ({
      x: x * geometry.width,
      y: y * geometry.height,
    }));
    const normalize = ({ x, y }: Point): Point => ({
      x: x / geometry.width,
      y: y / geometry.height,
    });
    const segments: VenueMapRouteSegment[] = [];
    let cursor = floorPoints[0];
    const lineTo = (end: Point) => {
      if (distance(cursor, end) > EPSILON)
        segments.push({ start: normalize(cursor), end: normalize(end) });
      cursor = end;
    };
    for (let i = 1; i < floorPoints.length - 1; i++) {
      const previous = floorPoints[i - 1];
      const corner = floorPoints[i];
      const next = floorPoints[i + 1];
      const before = distance(previous, corner);
      const after = distance(corner, next);
      const cornerClearance =
        [OPEN_AISLE_CLEARANCE, PREFERRED_CLEARANCE].find(
          (margin) =>
            clearSegment(previous, corner, margin) &&
            clearSegment(corner, next, margin),
        ) ?? CLEARANCE;
      let rounded = false;
      // Ignore straight or reversing segments; retain enough straight run on
      // either side for adjacent bends and the direction arrow.
      if (Math.abs(cross(previous, corner, next)) > before * after * 0.001) {
        for (
          let trim = Math.min(8, before / 3, after / 3);
          trim >= 0.5;
          trim /= 2
        ) {
          const entry = {
            x: corner.x + ((previous.x - corner.x) * trim) / before,
            y: corner.y + ((previous.y - corner.y) * trim) / before,
          };
          const exit = {
            x: corner.x + ((next.x - corner.x) * trim) / after,
            y: corner.y + ((next.y - corner.y) * trim) / after,
          };
          const triangle = [entry, corner, exit];
          // A quadratic stays inside its control triangle. Check the whole
          // triangle, including enclosed obstacles, rather than allowing a
          // visually smoother curve to cut through a booth corner.
          if (
            clearSegment(entry, corner, cornerClearance) &&
            clearSegment(corner, exit, cornerClearance) &&
            clearSegment(entry, exit, cornerClearance) &&
            !obstacles.some(({ points }) =>
              insidePolygon(points[0], triangle),
            ) &&
            (!constraints ||
              constraints.clearTriangle(entry, corner, exit, cornerClearance))
          ) {
            lineTo(entry);
            segments.push({
              start: normalize(entry),
              control: normalize(corner),
              end: normalize(exit),
            });
            cursor = exit;
            rounded = true;
            break;
          }
        }
      }
      if (!rounded) lineTo(corner);
    }
    lineTo(floorPoints.at(-1)!);
    return segments;
  }

  function explicitBoothAccess(location: MapLocation): Point | undefined {
    const footprints = obstacles.filter(
      (obstacle) => obstacle.id === location.id,
    );
    if (!footprints.length) return undefined;
    const access = {
      x: location.x * geometry.width,
      y: location.y * geometry.height,
    };
    const withinFootprint = footprints.some(
      ({ points }) =>
        insidePolygon(access, points) ||
        points.some(
          (point, i) =>
            pointSegmentDistanceSquared(
              access,
              point,
              points[(i + 1) % points.length],
            ) < EPSILON,
        ),
    );
    // Imported interior centers keep automatic aisle access. A point moved
    // outside the booth is an explicit navigation entry, not a new pin anchor.
    return withinFootprint ? undefined : access;
  }

  function aisleAccessPoints(
    id: string,
    anchor: Point,
    clearance: number,
    explicit?: Point,
  ): Point[] {
    const footprints = obstacles.filter((obstacle) => obstacle.id === id);
    if (!footprints.length)
      return clearSegment(anchor, anchor, clearance) ? [anchor] : [];
    if (explicit)
      return clearSegment(explicit, explicit, clearance) &&
        clearSegment(anchor, explicit, CLEARANCE, id)
        ? [explicit]
        : [];
    const candidates: Point[] = [];
    for (const { points } of footprints) {
      const area = points.reduce((sum, a, i) => {
        const b = points[(i + 1) % points.length];
        return sum + a.x * b.y - b.x * a.y;
      }, 0);
      for (let i = 0; i < points.length; i++) {
        const a = points[i],
          b = points[(i + 1) % points.length];
        const dx = b.x - a.x,
          dy = b.y - a.y;
        const length = distance(a, b);
        if (!length) continue;
        const nearest = Math.max(
          0,
          Math.min(
            1,
            ((anchor.x - a.x) * dx + (anchor.y - a.y) * dy) / length ** 2,
          ),
        );
        const offset = ((area >= 0 ? 1 : -1) * clearance) / length;
        // Edge samples retain access when a neighbour blocks the closest corner
        // or part of a concave boundary.
        for (const t of [nearest, 0.25, 0.5, 0.75]) {
          const point = {
            x: a.x + t * dx + dy * offset,
            y: a.y + t * dy - dx * offset,
          };
          if (
            clearSegment(point, point, clearance) &&
            // A fixed corner on a small booth can lie close to its neighbour.
            // Keep that short connector safe without narrowing the whole aisle.
            clearSegment(anchor, point, CLEARANCE, id) &&
            !candidates.some((other) => distance(other, point) < EPSILON)
          )
            candidates.push(point);
        }
      }
    }
    return candidates.sort((a, b) => distance(anchor, a) - distance(anchor, b));
  }

  function prepareLocation(location: MapLocation, clearance: number) {
    let prepared = locationAccess.get(location.id);
    // Known locations are validated before reaching here. Compare exact input
    // coordinates too: callers can differ within the validation epsilon.
    if (!prepared || prepared.x !== location.x || prepared.y !== location.y) {
      const pin = venueMapPinAnchor(location, geometry);
      prepared = {
        x: location.x,
        y: location.y,
        anchor: { x: pin.x * geometry.width, y: pin.y * geometry.height },
        explicit: explicitBoothAccess(location),
        points: new Map(),
      };
      locationAccess.set(location.id, prepared);
    }
    let points = prepared.points.get(clearance);
    if (!points) {
      points = aisleAccessPoints(
        location.id,
        prepared.anchor,
        clearance,
        prepared.explicit,
      );
      prepared.points.set(clearance, points);
    }
    return { anchor: prepared.anchor, explicit: prepared.explicit, points };
  }

  class MinHeap {
    private entries: { index: number; score: number }[] = [];

    push(index: number, score: number): void {
      const entry = { index, score };
      let i = this.entries.length;
      this.entries.push(entry);
      while (i > 0) {
        const parent = (i - 1) >> 1;
        if (this.entries[parent].score <= score) break;
        this.entries[i] = this.entries[parent];
        i = parent;
      }
      this.entries[i] = entry;
    }

    pop(): { index: number; score: number } | undefined {
      const first = this.entries[0];
      const last = this.entries.pop();
      if (!first || !last || !this.entries.length) return first;
      let i = 0;
      while (i * 2 + 1 < this.entries.length) {
        let child = i * 2 + 1;
        if (
          child + 1 < this.entries.length &&
          this.entries[child + 1].score < this.entries[child].score
        )
          child++;
        if (this.entries[child].score >= last.score) break;
        this.entries[i] = this.entries[child];
        i = child;
      }
      this.entries[i] = last;
      return first;
    }
  }

  /**
   * A schematic aisle route between explicit venue anchors, in normalized map units.
   * Short connectors join the fixed pin corners to nearby aisles. Every central
   * grid edge and smoothed shortcut avoids all stands, including the endpoints.
   * Missing or disconnected routes are omitted rather than drawn through stands.
   */
  function routeBetweenMapLocations(
    from: MapLocation,
    to: MapLocation,
  ): VenueMapRoutePoint[] | null {
    if (from.id === to.id) return null;
    for (const location of [from, to]) {
      const known = knownLocations.get(location.id);
      if (
        !known ||
        Math.abs(known.x - location.x) > EPSILON ||
        Math.abs(known.y - location.y) > EPSILON ||
        !Number.isFinite(location.x) ||
        !Number.isFinite(location.y)
      )
        return null;
    }
    const cacheKey = JSON.stringify([from.id, to.id]);
    if (routeCache.has(cacheKey)) return routeCache.get(cacheKey)!;
    const points =
      findRoute(from, to, PREFERRED_CLEARANCE) ??
      findRoute(from, to, CLEARANCE);
    const route =
      points?.map(({ x, y }) => ({
        x: x / geometry.width,
        y: y / geometry.height,
      })) ?? null;
    if (routeCache.size >= 64)
      routeCache.delete(routeCache.keys().next().value!);
    routeCache.set(cacheKey, route);
    return route;
  }

  function findRoute(
    from: MapLocation,
    to: MapLocation,
    clearance: number,
  ): Point[] | null {
    const fromPrepared = prepareLocation(from, clearance),
      toPrepared = prepareLocation(to, clearance);
    const start = fromPrepared.anchor,
      end = toPrepared.anchor;
    const locked = [fromPrepared.explicit, toPrepared.explicit].filter(
      (point): point is Point => point !== undefined,
    );
    const startAccess = fromPrepared.points,
      endAccess = toPrepared.points;
    if (!startAccess.length || !endAccess.length) return null;
    const nearby = (points: Point[], anchor: Point) =>
      points.filter(
        (point) =>
          distance(anchor, point) <= distance(anchor, points[0]) + GRID_STEP,
      );
    const nearStart = nearby(startAccess, start),
      nearEnd = nearby(endAccess, end);
    // Prefer short connectors. Farther edges are used only when the nearest
    // accessible edges cannot be joined through the aisle network.
    const aisle =
      findAisleRoute(start, end, nearStart, nearEnd, clearance) ??
      (nearStart.length < startAccess.length ||
      nearEnd.length < endAccess.length
        ? findAisleRoute(start, end, startAccess, endAccess, clearance)
        : null);
    if (!aisle) return null;
    const points = [start, ...aisle, end].filter(
      (point, i, path) => !i || distance(point, path[i - 1]) > EPSILON,
    );
    return spaceOpenAisles(
      alignAisleRuns(points, clearance, from.id, to.id, locked),
      clearance,
      from.id,
      to.id,
      locked,
    );
  }

  function prepareAisleGrid(clearance: number): PreparedAisleGrid {
    const existing = aisleGrids.get(clearance);
    if (existing) return existing;
    const clear = (a: Point, b: Point) => clearSegment(a, b, clearance);
    const doorwayGroups = constraints?.doorways(clearance, GRID_STEP) ?? [];
    const doorwayPoints: Point[] = [];
    const doorwayIndices = new Map<string, number>();
    const doorwayChains = doorwayGroups.map((group) =>
      group.map((point) => {
        const key = `${point.x.toFixed(8)},${point.y.toFixed(8)}`;
        const existing = doorwayIndices.get(key);
        if (existing !== undefined) return existing;
        const index = nodeCount + doorwayPoints.length;
        doorwayPoints.push(point);
        doorwayIndices.set(key, index);
        return index;
      }),
    );
    const searchNodeCount = nodeCount + doorwayPoints.length;
    const routePoint = (index: number) =>
      index < nodeCount ? gridPoint(index) : doorwayPoints[index - nodeCount];
    const availability = new Int8Array(searchNodeCount);
    function isAvailable(index: number): boolean {
      if (!availability[index]) {
        const point = routePoint(index);
        availability[index] = clear(point, point) ? 1 : -1;
      }
      return availability[index] === 1;
    }

    function connections(point: Point, includeDoors = true): number[] {
      const column = Math.round((point.x - minX) / stepX);
      const row = Math.round((point.y - minY) / stepY);
      const connected: number[] = [];
      for (
        let y = Math.max(0, row - 2);
        y <= Math.min(rows - 1, row + 2);
        y++
      ) {
        for (
          let x = Math.max(0, column - 2);
          x <= Math.min(columns - 1, column + 2);
          x++
        ) {
          const index = y * columns + x;
          if (isAvailable(index) && clear(point, routePoint(index)))
            connected.push(index);
        }
      }
      if (includeDoors)
        for (let i = 0; i < doorwayPoints.length; i++) {
          if (
            distance(point, doorwayPoints[i]) <= GRID_STEP * 4 &&
            isAvailable(nodeCount + i) &&
            clear(point, doorwayPoints[i])
          )
            connected.push(nodeCount + i);
        }
      return connected;
    }

    const doorwayLinks = new Map<number, number[]>();
    function link(a: number, b: number) {
      if (
        a === b ||
        !isAvailable(a) ||
        !isAvailable(b) ||
        !clear(routePoint(a), routePoint(b))
      )
        return;
      for (const [from, to] of [
        [a, b],
        [b, a],
      ]) {
        const links = doorwayLinks.get(from);
        if (links) {
          if (!links.includes(to)) links.push(to);
        } else doorwayLinks.set(from, [to]);
      }
    }
    for (let i = 0; i < doorwayPoints.length; i++) {
      const index = nodeCount + i;
      if (!isAvailable(index)) continue;
      for (const neighbor of connections(doorwayPoints[i]))
        link(index, neighbor);
    }
    // The side–center–side chain crosses the authored opening exactly. Every
    // edge still undergoes the same wall, booth and walkable-boundary checks.
    for (const chain of doorwayChains)
      for (let i = 1; i < chain.length; i++) link(chain[i - 1], chain[i]);

    // One known/pass bit per directed neighbor, independent of route endpoints.
    // Retain the predicate's direction even at floating-point clearance edges.
    // No endpoint booth exceptions or arbitrary smoothing segments enter this cache.
    const checkedEdges = new Uint8Array(nodeCount);
    const allowedEdges = new Uint8Array(nodeCount);
    function clearNeighbor(
      a: number,
      b: number,
      from: Point,
      to: Point,
    ): boolean {
      if (a >= nodeCount || b >= nodeCount) return clear(from, to);
      // Grid neighbors are emitted only by the adjacent-cell loop below.
      const slot =
        (Math.floor(b / columns) - Math.floor(a / columns) + 1) * 3 +
        (b % columns) -
        (a % columns) +
        1;
      const bit = 1 << (slot < 4 ? slot : slot - 1);
      if (!(checkedEdges[a] & bit)) {
        const allowed = clear(from, to);
        checkedEdges[a] |= bit;
        if (allowed) allowedEdges[a] |= bit;
      }
      return (allowedEdges[a] & bit) !== 0;
    }
    const prepared = {
      searchNodeCount,
      routePoint,
      isAvailable,
      connections,
      doorwayLinks,
      clearNeighbor,
    };
    aisleGrids.set(clearance, prepared);
    return prepared;
  }

  function findAisleRoute(
    start: Point,
    end: Point,
    startAccess: Point[],
    endAccess: Point[],
    clearance: number,
  ): Point[] | null {
    const clear = (a: Point, b: Point) => clearSegment(a, b, clearance);
    const orthogonal = (a: Point, b: Point): Point[] | null => {
      if (Math.abs(a.x - b.x) < EPSILON || Math.abs(a.y - b.y) < EPSILON)
        return clear(a, b) ? [a, b] : null;
      for (const elbow of [
        { x: a.x, y: b.y },
        { x: b.x, y: a.y },
      ]) {
        if (clear(a, elbow) && clear(elbow, b)) return [a, elbow, b];
      }
      return null;
    };
    let direct: Point[] | null = null;
    let directCost = Infinity;
    for (const a of startAccess)
      for (const b of endAccess) {
        const cost = distance(start, a) + distance(a, b) + distance(b, end);
        const connection =
          cost < directCost && clear(a, b) ? orthogonal(a, b) : null;
        if (connection) {
          direct = connection;
          directCost = cost;
        }
      }
    if (direct) return direct;

    const {
      searchNodeCount,
      routePoint,
      isAvailable,
      connections,
      doorwayLinks,
      clearNeighbor,
    } = prepareAisleGrid(clearance);

    const starts = new Map<number, { access: Point; cost: number }>();
    const ends = new Map<number, { access: Point; cost: number }>();
    for (const [accessPoints, anchor, connectionsByNode] of [
      [startAccess, start, starts],
      [endAccess, end, ends],
    ] as const) {
      for (const access of accessPoints)
        for (const index of connections(access)) {
          const cost =
            distance(anchor, access) + distance(access, routePoint(index));
          if (cost < (connectionsByNode.get(index)?.cost ?? Infinity))
            connectionsByNode.set(index, { access, cost });
        }
    }
    if (!starts.size || !ends.size) return null;
    const costs = new Float64Array(searchNodeCount).fill(Infinity);
    const previous = new Int32Array(searchNodeCount).fill(-1);
    const visited = new Uint8Array(searchNodeCount);
    const open = new MinHeap();
    for (const [index, connection] of starts) {
      const point = routePoint(index);
      costs[index] = connection.cost;
      open.push(index, costs[index] + distance(point, end));
    }
    let bestEnd = -1;
    let bestCost = Infinity;
    let next;
    while ((next = open.pop())) {
      if (next.score >= bestCost) break;
      const index = next.index;
      if (visited[index]) continue;
      visited[index] = 1;
      const point = routePoint(index);
      const connection = ends.get(index);
      if (connection && costs[index] + connection.cost < bestCost) {
        bestCost = costs[index] + connection.cost;
        bestEnd = index;
      }
      const neighbors = [...(doorwayLinks.get(index) ?? [])];
      if (index < nodeCount) {
        const column = index % columns;
        const row = Math.floor(index / columns);
        for (let dy = -1; dy <= 1; dy++)
          for (let dx = -1; dx <= 1; dx++) {
            if (
              (!dx && !dy) ||
              column + dx < 0 ||
              column + dx >= columns ||
              row + dy < 0 ||
              row + dy >= rows
            )
              continue;
            neighbors.push((row + dy) * columns + column + dx);
          }
      }
      for (const neighbor of neighbors) {
        if (visited[neighbor] || !isAvailable(neighbor)) continue;
        const neighborPoint = routePoint(neighbor);
        const cost = costs[index] + distance(point, neighborPoint);
        if (
          cost >= costs[neighbor] ||
          !clearNeighbor(index, neighbor, point, neighborPoint)
        )
          continue;
        costs[neighbor] = cost;
        previous[neighbor] = index;
        open.push(neighbor, cost + distance(neighborPoint, end));
      }
    }
    if (bestEnd < 0) return null;
    const path = [ends.get(bestEnd)!.access];
    let origin = bestEnd;
    for (let index = bestEnd; index >= 0; index = previous[index]) {
      path.push(routePoint(index));
      origin = index;
    }
    path.push(starts.get(origin)!.access);
    path.reverse();

    // Shortcuts follow the booth rows, with a checked right-angle turn when
    // needed. A short grid or booth-access connection can retain a diagonal
    // when a nearby obstacle leaves no room for an elbow.
    const simplified = [path[0]];
    let current = 0;
    while (current < path.length - 1) {
      let furthest = path.length - 1;
      let shortcut = orthogonal(path[current], path[furthest]);
      while (furthest > current + 1 && !shortcut) {
        furthest--;
        shortcut = orthogonal(path[current], path[furthest]);
      }
      simplified.push(...(shortcut?.slice(1) ?? [path[furthest]]));
      current = furthest;
    }
    return removeCollinearPoints(simplified);
  }

  return { roundedMapRouteSegments, routeBetweenMapLocations };
}

// Geometry belongs to one immutable map document. Weak references release route
// caches when an embed is destroyed or an edited draft replaces its document.
const documentContexts = new WeakMap<
  RoutingDocument,
  ReturnType<typeof createRoutingContext>
>();
const geometryContexts = new WeakMap<
  MapGeometry,
  ReturnType<typeof createRoutingContext>
>();

export function roundedMapRouteSegments(
  points: readonly VenueMapRoutePoint[],
  geometry: MapGeometry,
): VenueMapRouteSegment[] {
  let context = geometryContexts.get(geometry);
  if (!context) {
    context = createRoutingContext(geometry, []);
    geometryContexts.set(geometry, context);
  }
  return context.roundedMapRouteSegments(points);
}

export function routeBetweenMapLocations(
  from: MapLocation,
  to: MapLocation,
  document: RoutingDocument,
): VenueMapRoutePoint[] | null {
  let context = documentContexts.get(document);
  if (!context) {
    context = createRoutingContext(document.geometry, document.locations);
    documentContexts.set(document, context);
    geometryContexts.set(document.geometry, context);
  }
  return context.routeBetweenMapLocations(from, to);
}
